import { useCallback, useMemo } from 'react';
import { IconSelectFilled } from '@direct-frontend/components';
import { VersionSelect } from '#shared/components/VersionSelect';
import { format } from '#shared/lib/string';
import { constructorTestIds } from '#shared/lib/testids';
import { useGetSectionTemplateVersions } from '../../api';

export type ConstructorCustomSectionVersionSelectProps = {
    /** Id шаблона секции; для `new` селект не показывается. */
    sectionTemplateId: string;
    /** Текущая выбранная версия; если не задана — в списке выбирается самая свежая. */
    currentVersionId?: string;
    /** Смена версии в селекте (применение содержимого — на стороне родителя). */
    onApplyVersion: (versionId: string) => void;
};

export const ConstructorCustomSectionVersionSelect = ({
    sectionTemplateId,
    currentVersionId,
    onApplyVersion,
}: ConstructorCustomSectionVersionSelectProps) => {
    const skip = !sectionTemplateId || sectionTemplateId === 'new';

    const { data } = useGetSectionTemplateVersions({ id: sectionTemplateId }, { skip });

    const items = useMemo(() => {
        const versions = data?.versions ?? [];
        const sorted = [...versions].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        return sorted.map((version, index) => {
            return {
                value: String(version.version_id),
                content: format(version.created_at, 'pretty-datetime'),
                caption: `${version.author || ''}${version.author && index === 0 ? ' • ' : ''}${index === 0 ? 'Текущая версия' : ''}`,
                label: String(version.version_id),
                iconLeft: index === 0 ? <IconSelectFilled color="green" role="main" size="16" /> : undefined,
            };
        });
    }, [data?.versions]);

    const value = useMemo(() => {
        if (!items.length) {
            return '';
        }

        if (currentVersionId && items.some((item) => item.value === currentVersionId)) {
            return currentVersionId;
        }

        return items[0].value;
    }, [items, currentVersionId]);

    const onChange = useCallback(
        (versionId: string) => {
            onApplyVersion(versionId);
        },
        [onApplyVersion]
    );

    if (skip || !items.length) {
        return null;
    }

    return (
        <VersionSelect
            data-testid={constructorTestIds.customSectionVersionSelect}
            label="История версий секции"
            items={items}
            value={value}
            onChange={onChange}
            color="contrast"
        />
    );
};
